import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useFamilyContext } from '../context/FamilyContext';
import { useAuth } from '../context/AuthContext';

const { FiX, FiCalendar, FiRepeat, FiUser, FiFlag, FiLock, FiTag, FiBell, FiCheck } = FiIcons;

export default function TodoModal({ todo, onClose, onSave }) {
  const { state } = useFamilyContext();
  const { familyMembers } = state;
  const { canEdit } = useAuth();

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    dueDate: '',
    priority: 'medium',
    assignedTo: familyMembers[0]?.id || '',
    recurring: 'none',
    category: 'personal',
    reminders: [],
    completed: false
  });

  // CHECK PERMISSIONS
  const hasPermission = canEdit('todo', todo);
  
  useEffect(() => {
    if (todo) {
      setFormData({
        title: todo.title || '',
        description: todo.description || '',
        dueDate: todo.dueDate ? todo.dueDate.split('T')[0] : '',
        priority: todo.priority || 'medium',
        assignedTo: todo.assignedTo || familyMembers[0]?.id || '',
        recurring: todo.recurring || 'none',
        category: todo.category || 'personal',
        reminders: Array.isArray(todo.reminders) ? todo.reminders : [],
        completed: todo.completed || false
      });
    } else {
        // New Todo
        setFormData(prev => ({
            ...prev,
            assignedTo: familyMembers[0]?.id || ''
        }))
    }
  }, [todo, familyMembers]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!hasPermission) return;
    if (!formData.title.trim()) return;

    const todoData = {
      ...formData,
      dueDate: formData.dueDate ? new Date(formData.dueDate).toISOString() : null
    };
    onSave(todoData);
    onClose();
  };

  const handleComplete = () => {
      const todoData = {
          ...formData,
          completed: !formData.completed,
          completedAt: !formData.completed ? new Date().toISOString() : null,
          dueDate: formData.dueDate ? new Date(formData.dueDate).toISOString() : null
      };
      onSave(todoData);
      onClose();
  };

  const addReminder = (e) => {
    const val = e.target.value;
    if (val && !formData.reminders.includes(val)) {
        setFormData(prev => ({
            ...prev,
            reminders: [...prev.reminders, val]
        }));
    }
    e.target.value = "";
  };

  const removeReminder = (reminder) => {
      setFormData(prev => ({
          ...prev,
          reminders: prev.reminders.filter(r => r !== reminder)
      }));
  };

  const priorityOptions = [
    { value: 'low', label: 'Niedrig', color: 'text-green-600' },
    { value: 'medium', label: 'Mittel', color: 'text-yellow-600' },
    { value: 'high', label: 'Hoch', color: 'text-red-600' }
  ];

  const categoryOptions = [
      { value: 'personal', label: 'Persönlich' },
      { value: 'work', label: 'Arbeit' },
      { value: 'household', label: 'Haushalt' },
      { value: 'shopping', label: 'Einkauf' },
      { value: 'finance', label: 'Finanzen' },
      { value: 'health', label: 'Gesundheit' },
      { value: 'education', label: 'Schule/Bildung' },
      { value: 'other', label: 'Sonstiges' }
  ];

  const reminderOptions = [
      { value: 'at_time', label: 'Zum Zeitpunkt' },
      { value: '15_min', label: '15 Minuten vorher' },
      { value: '1_hour', label: '1 Stunde vorher' },
      { value: '1_day', label: '1 Tag vorher' },
      { value: '2_days', label: '2 Tage vorher' }
  ];

  const recurringOptions = [
    { value: 'none', label: 'Einmalig' },
    { value: 'daily', label: 'Täglich' },
    { value: 'weekly', label: 'Wöchentlich' },
    { value: 'biweekly', label: 'Alle 2 Wochen' },
    { value: 'monthly', label: 'Monatlich' },
    { value: 'yearly', label: 'Jährlich' }
  ];

  const getReminderLabel = (val) => reminderOptions.find(o => o.value === val)?.label || val;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-25" onClick={onClose} />
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="relative bg-white rounded-xl shadow-xl max-w-md w-full"
        >
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center">
              {todo ? 'Aufgabe bearbeiten' : 'Neue Aufgabe'}
              {!hasPermission && <SafeIcon icon={FiLock} className="ml-2 w-4 h-4 text-gray-400" title="Nur lesen" />}
            </h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <SafeIcon icon={FiX} className="w-6 h-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <fieldset disabled={!hasPermission} className="space-y-4">
                {/* Title */}
                <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    Titel *
                </label>
                <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 disabled:bg-gray-100"
                    placeholder="Aufgabe eingeben..."
                    required
                />
                </div>

                {/* Description */}
                <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    Beschreibung
                </label>
                <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows="3"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 disabled:bg-gray-100"
                    placeholder="Zusätzliche Details..."
                />
                </div>

                <div className="grid grid-cols-2 gap-4">
                    {/* Due Date */}
                    <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        <SafeIcon icon={FiCalendar} className="w-4 h-4 inline mr-2" />
                        Fälligkeit
                    </label>
                    <input
                        type="date"
                        value={formData.dueDate}
                        onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 disabled:bg-gray-100 text-sm"
                    />
                    </div>

                    {/* Recurring */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            <SafeIcon icon={FiRepeat} className="w-4 h-4 inline mr-2" />
                            Wiederholung
                        </label>
                        <select
                            value={formData.recurring}
                            onChange={(e) => setFormData({ ...formData, recurring: e.target.value })}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 disabled:bg-gray-100 text-sm"
                        >
                            {recurringOptions.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                    {/* Assigned To */}
                    <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        <SafeIcon icon={FiUser} className="w-4 h-4 inline mr-2" />
                        Zugewiesen
                    </label>
                    <select
                        value={formData.assignedTo}
                        onChange={(e) => setFormData({ ...formData, assignedTo: e.target.value })}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 disabled:bg-gray-100 text-sm"
                    >
                        {familyMembers.map(member => (
                        <option key={member.id} value={member.id}>{member.name}</option>
                        ))}
                    </select>
                    </div>

                    {/* Category */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            <SafeIcon icon={FiTag} className="w-4 h-4 inline mr-2" />
                            Kategorie
                        </label>
                        <select
                            value={formData.category}
                            onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 disabled:bg-gray-100 text-sm"
                        >
                            {categoryOptions.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Priority */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        <SafeIcon icon={FiFlag} className="w-4 h-4 inline mr-2" />
                        Priorität
                    </label>
                    <div className="flex space-x-2">
                        {priorityOptions.map(opt => (
                            <button
                                key={opt.value}
                                type="button"
                                onClick={() => setFormData({ ...formData, priority: opt.value })}
                                className={`flex-1 py-1.5 px-2 rounded-lg text-sm border ${
                                    formData.priority === opt.value 
                                    ? `bg-blue-50 border-blue-200 text-blue-700 ring-1 ring-blue-200` 
                                    : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                                }`}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Reminders Section */}
                <div>
                     <label className="block text-sm font-medium text-gray-700 mb-2">
                        <SafeIcon icon={FiBell} className="w-4 h-4 inline mr-2" />
                        Erinnerungen
                    </label>
                    
                    <div className="flex space-x-2 mb-2">
                        <select 
                            onChange={addReminder} 
                            className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm disabled:bg-gray-100"
                            defaultValue=""
                            disabled={!hasPermission}
                        >
                            <option value="" disabled>Erinnerung hinzufügen...</option>
                            {reminderOptions.map(opt => (
                                <option key={opt.value} value={opt.value} disabled={formData.reminders.includes(opt.value)}>
                                    {opt.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    {formData.reminders.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                            {formData.reminders.map((rem, idx) => (
                                <div key={idx} className="flex items-center bg-blue-50 text-blue-700 px-2 py-1 rounded-md text-xs border border-blue-100">
                                    <span>{getReminderLabel(rem)}</span>
                                    {hasPermission && (
                                        <button 
                                            type="button" 
                                            onClick={() => removeReminder(rem)}
                                            className="ml-1 text-blue-400 hover:text-blue-600"
                                        >
                                            <SafeIcon icon={FiX} className="w-3 h-3" />
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

            </fieldset>

            {/* Actions */}
            <div className="flex items-center justify-between pt-4 border-t border-gray-100 mt-4">
               <div>
                  {todo && hasPermission && (
                      <button
                        type="button"
                        onClick={handleComplete}
                        className={`flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                            formData.completed 
                            ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                            : 'bg-gray-100 text-gray-700 hover:bg-green-100 hover:text-green-700'
                        }`}
                      >
                         <SafeIcon icon={FiCheck} className="w-4 h-4 mr-2" />
                         {formData.completed ? 'Erledigt' : 'Erledigen'}
                      </button>
                  )}
               </div>
               <div className="flex space-x-3">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                        {hasPermission ? 'Abbrechen' : 'Schließen'}
                    </button>
                    {hasPermission && (
                        <button
                            type="submit"
                            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                        >
                            {todo ? 'Speichern' : 'Erstellen'}
                        </button>
                    )}
               </div>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
}